import { motion } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { RESTAURANT_INFO } from "@/data/menuData";

export const ContactSection = () => {
  return (
    <section data-testid="contact-section" className="bg-[#F5F5F5] py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="font-['DM_Sans'] text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#C41E3A]">
            Visítanos
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#2B3618] mt-3 tracking-tight">
            Ubicación y Contacto
          </h2>
          <div className="w-16 h-1 bg-[#C41E3A] rounded-full mx-auto mt-4" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Location Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-[#1A1A1A]/5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#556B2F]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#556B2F]" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1A1A1A]">Dirección</h3>
                <p className="font-['DM_Sans'] text-sm text-[#4A4A4A] mt-2 leading-relaxed">
                  {RESTAURANT_INFO.address}
                </p>
                <a
                  href="https://maps.app.goo.gl/SXgEdSoVREohQSGLA?g_st=ic"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="map-link"
                  className="inline-flex items-center gap-1 font-['DM_Sans'] text-sm font-semibold text-[#C41E3A] hover:text-[#9A172D] mt-3 transition-colors duration-300"
                >
                  Ver en Google Maps
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Phone Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-[#1A1A1A]/5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#556B2F]/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-[#556B2F]" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1A1A1A]">Teléfono</h3>
                <a
                  href={`tel:${RESTAURANT_INFO.phone}`}
                  data-testid="phone-link"
                  className="block font-['DM_Sans'] text-lg font-semibold text-[#1A1A1A] mt-2 hover:text-[#556B2F] transition-colors duration-300"
                >
                  {RESTAURANT_INFO.phoneDisplay}
                </a>
                <div className="mt-4 pt-4 border-t border-[#1A1A1A]/8">
                  <h4 className="font-['DM_Sans'] text-sm font-semibold text-[#1A1A1A]">WhatsApp</h4>
                  <a
                    href={`https://wa.me/${RESTAURANT_INFO.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="inline-flex items-center gap-2 font-['DM_Sans'] text-lg font-semibold text-[#556B2F] hover:text-[#2B3618] mt-1 transition-colors duration-300"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {RESTAURANT_INFO.whatsappDisplay}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
